import React from 'react';
import { Container, Typography, Box, Paper } from '@mui/material';

const TermsPage = () => {
  return (
    <Container maxWidth="md">
      <Paper sx={{ p: { xs: 3, md: 5 }, mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          General Terms and Conditions (Terms of Service) – GrooveGate
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Version: July 8, 2025
        </Typography>

        <Box sx={{ '& > *': { mb: 3 } }}>
          <Box>
            <Typography variant="h5" gutterBottom>
              1. Scope and Provider
            </Typography>
            <Typography paragraph>
              1.1 These General Terms and Conditions (Terms of Service) apply to the use of the platform "GrooveGate" (hereinafter "the platform") by registered users (hereinafter "users").
            </Typography>
            <Typography paragraph>
              1.2 The platform is operated by GrooveGate UG (haftungsbeschränkt), represented by Jonas Morgenstern & Nils Gies, Warschauer Str. 59A, 10243 Berlin, Germany.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              2. Service Description
            </Typography>
            <Typography paragraph>
              2.1 GrooveGate is a digital platform for music labels, artists, and curators. It enables the upload, targeted promotion, distribution, and optional sale of digital content. Functions include in particular the upload of content (e.g., audio files, texts, graphics), targeted sending of promos, sale of digital products, and tracking of interactions by third parties.
            </Typography>
            <Typography paragraph>
              2.2 Individual functions may require separate agreements (e.g., label partnerships or extended distribution options).
            </Typography>
            <Typography paragraph>
              2.3 GrooveGate strives for high availability of the platform but cannot guarantee uninterrupted accessibility.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              3. Registration and User Account
            </Typography>
            <Typography paragraph>
              3.1 Use of the platform requires complete registration. All information must be correct and current. Users are obligated to continuously update their data.
            </Typography>
            <Typography paragraph>
              3.2 Access credentials must be kept confidential. The user is responsible for the security of access. In case of unauthorized access, GrooveGate must be notified immediately.
            </Typography>
            <Typography paragraph>
              3.3 Use of the platform is only permitted for adults (18 years and older).
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              4. Content and Usage Rights
            </Typography>
            <Typography paragraph>
              4.1 Users are solely responsible for all uploaded content and warrant that they have all necessary rights (particularly copyrights, related rights, and synchronization rights if applicable).
            </Typography>
            <Typography paragraph>
              4.2 Uploading copyrighted content of third parties without appropriate license is prohibited.
            </Typography>
            <Typography paragraph>
              4.3 Users grant GrooveGate a non-exclusive, worldwide, transferable, sublicensable, and royalty-free usage right to store, host, transmit, publicly perform, display, distribute, promote, provide, and technically process the content (e.g., for analysis or format conversion) as long as the content is active on the platform.
            </Typography>
            <Typography paragraph>
              4.4 GrooveGate reserves the right to block or delete content upon concrete indications of legal violations. Rights holders can report copyright infringements through a complaint procedure ("Notice and Takedown").
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              5. User Behavior
            </Typography>
            <Typography paragraph>
              5.1 It is prohibited to distribute content that violates applicable law or infringes third-party rights (e.g., insulting, violence-glorifying, pornographic, or copyright-infringing content).
            </Typography>
            <Typography paragraph>
              5.2 Sending spam and using purchased or generated contact lists without prior express consent of recipients is prohibited. When sending emails via the platform, valid consents (ideally through double opt-in procedure) must be in place.
            </Typography>
            <Typography paragraph>
              5.3 In case of violations, GrooveGate may restrict functions, temporarily suspend, or permanently delete user accounts.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              6. Prices, Payment, and Billing
            </Typography>
            <Typography paragraph>
              6.1 Use of the platform may be subject to fees. The currently valid prices and services are shown in the current price list on the GrooveGate website.
            </Typography>
            <Typography paragraph>
              6.2 Billing is done on a subscription model. Monthly fees are automatically charged at the beginning of each billing period via the stored payment method.
            </Typography>
            <Typography paragraph>
              6.3 In case of failed payment collection, GrooveGate may temporarily restrict access to the platform. The payment obligation remains unaffected.
            </Typography>
            <Typography paragraph>
              6.4 GrooveGate reserves the right to change prices with reasonable advance notice. Changes will be communicated by email at least 30 days before taking effect and apply from the next billing period.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              7. Liability
            </Typography>
            <Typography paragraph>
              7.1 GrooveGate is not liable for content uploaded or distributed by users. Upon knowledge of legal violations, corresponding content will be removed.
            </Typography>
            <Typography paragraph>
              7.2 GrooveGate is only liable for damages in case of intent or gross negligence. In case of simple negligence, GrooveGate is only liable for violation of essential contractual obligations ("cardinal obligations") and limited to typically foreseeable damage.
            </Typography>
            <Typography paragraph>
              7.3 For data loss, GrooveGate is only liable if the user proves to have performed regular and complete backups.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              8. Contract Duration and Termination
            </Typography>
            <Typography paragraph>
              8.1 The contract duration depends on the selected pricing model (e.g., monthly cancellable subscription).
            </Typography>
            <Typography paragraph>
              8.2 Users may cancel the subscription at any time with effect at the end of the current billing month. Already made payments will not be refunded.
            </Typography>
            <Typography paragraph>
              8.3 GrooveGate may terminate the contractual relationship with 14 days' notice or immediately in case of serious violations (e.g., legal violations, payment default).
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              9. Data Protection
            </Typography>
            <Typography paragraph>
              9.1 The processing of personal data is done according to GrooveGate's privacy policy. This is available on the website at any time.
            </Typography>
            <Typography paragraph>
              9.2 Users may not upload or process personal data of third parties without their express consent.
            </Typography>
            <Typography paragraph>
              9.3 When sending emails via GrooveGate, users must ensure that express consent from recipients is in place. The use of double opt-in procedure is strongly recommended and may be required for certain functions.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              10. Final Provisions
            </Typography>
            <Typography paragraph>
              10.1 The law of the Federal Republic of Germany applies. Insofar as legally permissible, the place of jurisdiction is Berlin.
            </Typography>
            <Typography paragraph>
              10.2 Should individual provisions of these Terms of Service be or become invalid, the validity of the remaining provisions remains unaffected.
            </Typography>
            <Typography paragraph>
              10.3 GrooveGate reserves the right to change these Terms of Service in the future. Changes will be communicated by email to the registered address. If users do not object within 14 days, the changes are considered accepted.
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default TermsPage;
